import { isTravelBallFamilyRole, type TravelBallFamilyRole } from '../../../types/travelBallFamily';
import { isFreshTravelBallSignup } from './tbSignupStorage';

/**
 * Keeps the travel ball player/parent signup across a refresh of the verify step, and across tabs.
 * Same rules as `tbProgramSignupStorage.ts`: localStorage because the verification link opens a
 * second tab, bound to the Firebase account's address, cleared on sign-out, **never the password**,
 * and every access wrapped so blocked site data degrades to "no resume". Like the program signup it
 * carries `savedAt` and expires after `TRAVEL_BALL_SIGNUP_MAX_AGE_MS` (see
 * `tbSignupStorage.ts`); a blob without one is discarded.
 */

const STORAGE_KEY = 'rosterpilot.travelBallFamilySignup';

export interface StoredTravelBallFamilySignup {
  step: 'verify';
  email: string;
  familyRole: TravelBallFamilyRole;
  firstName: string;
  lastName: string;
  /** The form cannot be sent without agreeing, so a stored signup always carries it. */
  acceptedTerms: true;
}

export const saveTravelBallFamilySignup = (state: StoredTravelBallFamilySignup): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch {
    // Storage unavailable: a refresh will start over, which is safe.
  }
};

export const clearTravelBallFamilySignup = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
};

/** The stored signup, only when it belongs to `email` (the signed-in Firebase account). */
export const loadTravelBallFamilySignup = (
  email: string | null | undefined,
): StoredTravelBallFamilySignup | null => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  // Firebase may not have restored the session yet on a cold load: keep the blob for later rather
  // than dropping it as someone else's.
  if (!email) return null;

  try {
    const parsed = JSON.parse(raw) as
      | (Partial<Record<keyof StoredTravelBallFamilySignup, unknown>> & { savedAt?: unknown })
      | null;
    if (
      parsed?.step === 'verify' &&
      isFreshTravelBallSignup(parsed.savedAt) &&
      typeof parsed.email === 'string' &&
      isTravelBallFamilyRole(parsed.familyRole) &&
      typeof parsed.firstName === 'string' &&
      typeof parsed.lastName === 'string' &&
      parsed.acceptedTerms === true &&
      parsed.email.toLowerCase() === email.toLowerCase()
    ) {
      return {
        step: 'verify',
        email: parsed.email,
        familyRole: parsed.familyRole,
        firstName: parsed.firstName,
        lastName: parsed.lastName,
        acceptedTerms: true,
      };
    }
  } catch {
    // Malformed: fall through and drop it.
  }

  clearTravelBallFamilySignup();
  return null;
};
